import { createContext, useContext, useMemo, useEffect, type ReactNode } from 'react';
import { autorun } from 'mobx';
import * as Sentry from '@sentry/react-native';

import RootStore, { IRootStore } from './RootStore';
import { setOnUnauthorized } from '@/services/api/client';
import {
  setLogStreamingEnabled,
  resetLogStreaming,
} from '@/services/logging/logStreaming';

const StoreContext = createContext<IRootStore | null>(null);

export function useStores(): IRootStore {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('useStores must be used within a StoreProvider');
  }
  return store;
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const rootStore = useMemo(() => RootStore.create({}), []);

  useEffect(() => {
    setOnUnauthorized(() => rootStore.userStore.clear());
  }, [rootStore]);

  useEffect(() => {
    const dispose = autorun(() => {
      const user = rootStore.userStore.currentUser;
      if (user) {
        Sentry.setUser({
          id: String(user.id),
          email: user.email ?? undefined,
        });
        // Reading `shouldStreamTvLogs` here makes this autorun re-fire when the user toggles
        // their opt-in, so the Sentry Logs gate flips immediately (non-prod streams anyway).
        setLogStreamingEnabled(user.shouldStreamTvLogs);
      } else {
        Sentry.setUser(null);
        resetLogStreaming();
      }
    });
    return dispose;
  }, [rootStore]);

  return (
    <StoreContext.Provider value={rootStore}>{children}</StoreContext.Provider>
  );
}
